import { modalActions } from '@/actions';
import Fab from '@/components/elements/forms/buttons/Fab/Fab';
import { usePinInput } from '@/components/elements/forms/PinInput/PinInput';
import Icon from '@/components/icons/Icon';
import CreateAccount from '@/components/templates/login/CreateAccount';
import RequestToken from '@/components/templates/login/RequestToken';
import VerifyOTP from '@/components/templates/login/VerifyOTP';
import VerifySuccess from '@/components/templates/login/VerifySuccess';
import { loginTrackingSession } from '@/helpers/loginTrackingSession';
import { LoginContext, LoginContextType, View } from '@/hooks/useLogin';
import { _s } from '@/locale';
import { Dispatch, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Modal, { ModalProps } from './redesign/Modal/Modal';

type LoginModalProps = ModalProps & {
  floating?: boolean;
};

export const baseTranslationKey = (view: View) => `loginModal.${view}`;

const closeLoginModal = (ctx: LoginContextType, dispatch: Dispatch<any>, cancelTrackingSession = true) => {
  dispatch(modalActions.login({ show: false }));
  ctx.clearLoginState();
  ctx.onClose(cancelTrackingSession);
};

const LoginModal = ({ isOpen }: LoginModalProps) => {
  const { view } = useContext(LoginContext);
  return (
    <Modal isOpen={isOpen} shouldCloseOnOverlayClick={false}>
      {view === 'requestToken' && <RequestTokenContent />}
      {view === 'createAccount' && <CreateAccountContent />}
      {view === 'verifyOtp' && <VerifyOTPContent />}
      {view === 'verifySuccess' && <VerifySuccessContent />}
    </Modal>
  );
};

const VerifySuccessContent = () => {
  const ctx = useContext(LoginContext);

  const dispatch = useDispatch();

  const handleCloseLoginModal = () => {
    closeLoginModal(ctx, dispatch, false /** cancelTrakingSession */);
  };

  return (
    <Modal.Content floating gradientBackground>
      <div className="gap-md flex flex-col">
        <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(ctx.view)}.title`)}</h2>
        <VerifySuccess email={ctx.email} onAnimationComplete={handleCloseLoginModal} />
      </div>
    </Modal.Content>
  );
};

const VerifyOTPContent = () => {
  const ctx = useContext(LoginContext);
  const { email, view, updateView, handleEmailResendClicked, user, flow } = ctx;

  const pinProps = usePinInput({ fields: 6, validateCallback: ctx.handleLogin });

  const handleGoBack = () => {
    loginTrackingSession.track({ step: 'back_to_email_submission', flow });
    ctx.updateView('requestToken');
    ctx.clearLoginState();
  };

  useEffect(() => {
    if (user) updateView('verifySuccess');
  }, [user, view]);

  return (
    <Modal.Content floating gradientBackground>
      <div className="gap-md flex flex-col">
        <div className="gap-lg flex items-start">
          <Fab
            onClick={handleGoBack}
            variant="secondary"
            icon={<Icon variant="back" />}
            size="md"
            aria-label={_s('back')}
          />
        </div>
        <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(view)}.title`)}</h2>
        <VerifyOTP {...pinProps} email={email} handleEmailResendClicked={handleEmailResendClicked} />
      </div>
    </Modal.Content>
  );
};

const RequestTokenContent = () => {
  const ctx = useContext(LoginContext);
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const handleCloseLoginModal = () => {
    closeLoginModal(ctx, dispatch);
  };

  return (
    <Modal.Content floating>
      <div className="flex flex-col">
        <div className="gap-lg pb-xl flex items-start justify-between">
          <div className="bg-bg_info_bold grid place-content-center rounded-full" style={{ width: 56, height: 56 }}>
            <Icon variant="account" color="fg_alwaysLight" size="md" />
          </div>
          <Fab
            onClick={handleCloseLoginModal}
            variant="secondary"
            size="md"
            icon={<Icon variant="close-alt" />}
            aria-label={_s('close')}
          />
        </div>
        <h2 className="text-fg_primary text-h-m pb-sm">{_s(`${baseTranslationKey(ctx.view)}.title`)}</h2>
        <RequestToken {...ctx} redirectUrl={pathname} autoFocus />
      </div>
    </Modal.Content>
  );
};

const CreateAccountContent = () => {
  const ctx = useContext(LoginContext);
  const { pathname } = useLocation();

  const handleOnBackClick = () => {
    loginTrackingSession.track({ step: 'back_to_email_submission', flow: 'sign-up' });
    ctx.updateView('requestToken');
  };

  return (
    <Modal.Content floating>
      <div className="gap-md flex flex-col">
        <div className="gap-lg flex items-start">
          <Fab
            onClick={handleOnBackClick}
            variant="secondary"
            icon={<Icon variant="back" />}
            size="md"
            aria-label={_s('back')}
          />
        </div>
        <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(ctx.view)}.title`)}</h2>
        <CreateAccount {...ctx} redirectUrl={pathname} />
      </div>
    </Modal.Content>
  );
};

export default LoginModal;
